import {Injectable} from '@angular/core';
import {DipRegistrationAppContext} from '../models/dip-registration-app-context.model';
import {DipRegistrationContext} from '../models/dip-registration-context.model';
import {
  DIPRS_CONTEXT_TOKEN,
  getApiUrl,
  getUrlParameter,
  joinObjectKeyValues
} from './utilities/dip-registration-utility';
import { HttpResponse } from '@angular/common/http';
import {DipRegistrationLoggerService} from './logger/dip-registration-logger.service';
import {DipRegistrationUAPState} from '../models/uap/dip-registration-uap-state.model';


export const CSRF_HEADER = 'x-csrf-header';
export const CSRF_TOKEN = 'x-csrf-token';
export const SESSION_ID = 'x-isid';
export const APP_VERSION = 'x-app-version';

@Injectable()
export class DipRegistrationContextService {
  private appContext: DipRegistrationAppContext;

  constructor(private loggerService: DipRegistrationLoggerService) {
  }

  private getContext(): DipRegistrationContext {
    return this.appContext?.context || {} as DipRegistrationContext;
  }

  initAppContext(): void {
    this.appContext = new DipRegistrationAppContext();
  }

  setPhoneNumber(phoneNumber: string): void {
    this.appContext.phoneNumber = phoneNumber;
  }

  setWcmLoaded(value: boolean): void {
    this.appContext.wcmLoaded = value;
  }

  setTmxId(value: string): void {
    this.appContext.tmxId = value;
  }

  getTmxId(): string {
    return this.appContext.tmxId;
  }

  getRemainingSessionTimeout(): number {
    return this.getContext().sessionTimeout - this.getSessionWarningTimeout();
  }

  resetContextFlag(): void {
    this.appContext.forceReset = true;
  }

  getUrpUrl(): string {
    return `${this.getContext().urpUrl}?lang=${this.appContext.language}`;
  }

  getConsumerAppId(): string {
    return this.getContext().uapConfig?.consumerAppId || null;
  }

  getClientId(): string {
    return this.getContext().uapConfig?.clientId || null;
  }

  getSessionId(): string {
    return this.appContext.sessionId;
  }

  getVersionId(): string {
    return this.appContext.version;
  }

  getAppContext(): DipRegistrationAppContext {
    return this.appContext;
  }

  private buildState(): string {
    const redirectUrlParams = {
      skipMaintenanceMode: this.appContext.skipMaintenanceMode,
      skipSystemAvailability: this.appContext.skipSystemAvailability,
      disableThirdPartyScripts: this.appContext.disableThirdPartyScripts,
      language: this.appContext.languageFromUrl ? this.appContext.language : null,
      environmentId: this.appContext.environmentId,
      bbfpId: this.getTmxId(),
      dipssGuid: this.appContext.dipssGuid
    } as DipRegistrationUAPState;
    const cleanObject = {};
    Object
      .keys(redirectUrlParams)
      .forEach(key => {
        if (redirectUrlParams[key] !== null && redirectUrlParams[key] !== undefined) {
          cleanObject[key] = redirectUrlParams[key];
        }
      });

    return btoa(JSON.stringify(cleanObject));
  }

  getUapUrl(): string {
    const uapConfig = this.getContext().uapConfig;
    if (uapConfig) {
      const params = {
        lang: `${this.appContext.language}_CA`,
        tsnConsumerAppId: `${uapConfig.consumerAppId}`,
        consumer: `${uapConfig.consumer}`,
        client_id: `${uapConfig.clientId}`,
        pfidpadapterid: `${uapConfig.pfidpadapterid}`,
        scope: `${uapConfig.scope}`,
        redirect_uri: getApiUrl(),
        response_type: `${uapConfig.responseType}`,
        nonce: `${uapConfig.nonce}`,
        state: this.buildState()
      };
      const queryStrings = joinObjectKeyValues(params, '&');
      return `${uapConfig.baseUrl}?${queryStrings}`;
    }
    return null;
  }

  getMaintenancePageUrl(): string {
    return this.appContext.language === 'fr' ? 'https://www.tdassurance.com/sitemaintenance/inscription-client' : 'https://www.tdinsurance.com/sitemaintenance/customer-registration';
  }

  getSessionWarningTimeout(): number {
    return this.getContext().sessionWarningTimeout;
  }

  getSessionTimeout() {
    return this.getContext().sessionTimeout;
  }

  getCsrfInfo(): { csrfHeader: string, csrfToken: string } {
    return (this.appContext.csrfHeader && this.appContext.csrfToken) ? {
      csrfHeader: this.appContext.csrfHeader,
      csrfToken: this.appContext.csrfToken
    } : null;
  }

  getEnvironmentId(): string {
    return this.appContext.environmentId;
  }

  getPhoneNumber(): string {
    return this.appContext.phoneNumber;
  }

  isMetricsEnabled(): boolean {
    return this.getContext().metricsEnabled;
  }

  setLanguage(language: string) {
    this.appContext.language = language;
  }

  getLanguage(): string {
    return this.appContext.language;
  }

  setIsAutoEnrollFlow(isAutoEnrollFlow: boolean) {
    this.appContext.isAutoEnrollFlow = isAutoEnrollFlow;
  }

  getIsAutoEnrollFlow(): boolean {
    return this.appContext.isAutoEnrollFlow;
  }

  isContextLoaded(): boolean {
    return !!(this.appContext && this.appContext.context && this.appContext.wcmLoaded);
  }

  isAuthenticated(): boolean {
    return this.getContext().authenticated;
  }

  setAuthenticated(authenticated: boolean): void {
    this.getContext().authenticated = authenticated;
  }

  isMaintenanceMode(): boolean {
    return this.getContext().maintenanceMode && !this.appContext.skipMaintenanceMode;
  }

  isForceReset(): boolean {
    return this.appContext.forceReset;
  }

  // Load state from uap redirect url
  public loadState(state: string): DipRegistrationUAPState {
    if (state) {
      try {
        return JSON.parse(atob(state)) as DipRegistrationUAPState;
      } catch (error) {
        this.loggerService.error('Error loading state from uap', error);
      }
    }
    return null;
  }

  /**
   * call server side to get a context and return the context
   * @param contextResponse http response of the get context
   * @param uapState the state object returned from uap
   */
  loadContext(contextResponse: HttpResponse<DipRegistrationContext>, uapState?: DipRegistrationUAPState): DipRegistrationContext {
    if (contextResponse && contextResponse.body) {
      this.appContext.context = contextResponse.body;
      this.appContext.csrfHeader = contextResponse.headers.get(CSRF_HEADER);
      this.appContext.csrfToken = contextResponse.headers.get(CSRF_TOKEN);
      this.appContext.sessionId = contextResponse.headers.get(SESSION_ID);
      this.appContext.version = contextResponse.headers.get(APP_VERSION);
      this.appContext.environmentId = this.getEnvIdFromUrl();
      this.appContext.disableThirdPartyScripts = this.isDisableThirdPartyScripts();
      this.appContext.skipSystemAvailability = this.isSkipSystemAvailability();
      this.initLanguageFromUrl(uapState);
      this.initSkipMaintenanceModeFlag();
      this.appContext.loadFromUAPState(uapState);
      window[DIPRS_CONTEXT_TOKEN] = contextResponse.body;
      const dipssGuid = getUrlParameter('dipssGuid');
      if (dipssGuid) {
        this.appContext.dipssGuid = dipssGuid;
      }
      return contextResponse.body;
    }
    return null;
  }

  isDisableThirdPartyScripts() {
    return getUrlParameter('disableThirdPartyScripts') === 'true';
  }

  isSkipSystemAvailability() {
    return getUrlParameter('skipSystemAvailability') === 'true';
  }

  private initSkipMaintenanceModeFlag(): void {
    if (this.getContext().maintenanceMode) {
      const skipMaintenanceMode = getUrlParameter('skipMaintenanceMode');
      if (skipMaintenanceMode === 'true') {
        this.appContext.skipMaintenanceMode = true;
      }
    }
  }

  private getEnvIdFromUrl() {
    return getUrlParameter('environmentId');
  }

  buildParams(state?: any): { queryParams: any, state: any } {
    const params: { queryParams: any, state: any } = {queryParams: {}, state: ''};
    const envId = this.getEnvIdFromUrl() || this.appContext.environmentId;
    if (envId) {
      params.queryParams = {...params.queryParams, environmentId: envId};
    }

    const disableThirdPartyScripts = getUrlParameter('disableThirdPartyScripts') || this.appContext.disableThirdPartyScripts;
    if (disableThirdPartyScripts) {
      params.queryParams = {...params.queryParams, disableThirdPartyScripts};
    }

    const language = getUrlParameter('language') || this.appContext.language;
    if (language && this.appContext.languageFromUrl) {
      params.queryParams = {...params.queryParams, language};
    }

    const skipMaintenanceMode = getUrlParameter('skipMaintenanceMode') || this.appContext.skipMaintenanceMode;
    if (skipMaintenanceMode) {
      params.queryParams = {...params.queryParams, skipMaintenanceMode};
    }

    const skipSystemAvailability = getUrlParameter('skipSystemAvailability') || this.appContext.skipSystemAvailability;
    if (skipSystemAvailability) {
      params.queryParams = {...params.queryParams, skipSystemAvailability};
    }
    if (state) {
      params.state = state;
    }
    return params;
  }

  buildQueryString(): string {
    const params = [];
    const envId = this.getEnvIdFromUrl() || this.appContext.environmentId;
    if (envId) {
      params.push(`environmentId=${envId}`);
    }

    const disableThirdPartyScripts = getUrlParameter('disableThirdPartyScripts') || this.appContext.disableThirdPartyScripts;
    if (disableThirdPartyScripts) {
      params.push(`disableThirdPartyScripts=${disableThirdPartyScripts}`);
    }

    const language = getUrlParameter('language') || this.appContext.language;
    if (language && this.appContext.languageFromUrl) {
      params.push(`language=${language}`);
    }

    const skipMaintenanceMode = getUrlParameter('skipMaintenanceMode') || this.appContext.skipMaintenanceMode;
    if (skipMaintenanceMode) {
      params.push(`skipMaintenanceMode=${skipMaintenanceMode}`);
    }

    const skipSystemAvailability = getUrlParameter('skipSystemAvailability') || this.appContext.skipSystemAvailability;
    if (skipSystemAvailability) {
      params.push(`skipSystemAvailability=${skipSystemAvailability}`);
    }
    return params.length > 0 ? '?' + params.join('&') : '';
  }

  /**
   * This function return the language using the following logic
   * if a language query parameter exists it checks if it's fr and return it else it will return en
   * or will check the partial domain if it's a french partial domain it will return fr or return en
   * the query param language will take precedence over the partial domain one
   * @return the language
   */
  private initLanguageFromUrl(uap): void {
    const uapStateLang = uap ? uap.language : null;
    const languageQueryParam = getUrlParameter('language') || uapStateLang;
    if (languageQueryParam) {
      this.appContext.language = languageQueryParam === 'fr' ? 'fr' : 'en';
      this.appContext.languageFromUrl = true;
    } else {
      const frenchPartialDomain = this.getContext().frenchPartialDomain || 'monassurance';
      this.appContext.language = window.location.href.indexOf(frenchPartialDomain) > -1 ? 'fr' : 'en';
      this.appContext.languageFromUrl = false;
    }
  }
}
